import * as THREE from 'three';
import { settings } from '@/config';
import type { Camera } from '@/camera/Camera';
import type { TickState } from '@/types';

/**
 * CPU-side state for one puff. Only `pos` changes per frame (drift + wrap); everything else is
 * written once into a static instanced attribute at spawn.
 */
interface Puff {
  pos: THREE.Vector3;
  /** Height-tinted base colour (light/dark blend) BEFORE the distance haze mix. */
  baseColor: THREE.Color;
  baseOpacity: number;
}

/**
 * SOFT-PARTICLE + NOISE shader injection for the puff sprites (2026-07-17, user: where the
 * cloud texture "collides with my models … it's so buggy", they want it to "feel like noise
 * and fog when those clouds passes through my scene mesh").
 *
 * A sprite is a flat quad, so where it intersects a mesh the GPU slices it along a hard
 * straight line — the tell-tale "cardboard through wall" seam. Two additions fix that,
 * WITHOUT abandoning the sprite technique:
 *
 *  1. SOFT PARTICLES — compare this fragment's view depth against the solid scene's depth
 *     ({@link SceneDepth}) and fade the puff out as it nears the surface, so it dissolves
 *     into geometry rather than cutting through it.
 *  2. NOISE EROSION — break that fade up with animated fBM so the contact zone frays like
 *     vapour instead of reading as a clean alpha ramp. Deliberately applied ONLY near the
 *     contact (weighted by `1 - soft`): eroding the whole puff would make every cloud look
 *     moth-eaten.
 *
 * Injected via `onBeforeCompile` into three's stock sprite shader — the billboarding,
 * texture and fog handling all stay stock. The depth/resolution/camera uniforms are SHARED
 * objects across all ~300 puff materials (one write updates every puff, and identical shader
 * source means the GPU still compiles ONE program); only `uPhase` is per-puff, so their noise
 * fields don't march in lockstep.
 */
interface SoftUniforms {
  uDepth: THREE.IUniform<THREE.Texture | null>;
  uResolution: THREE.IUniform<THREE.Vector2>;
  uCameraNear: THREE.IUniform<number>;
  uCameraFar: THREE.IUniform<number>;
  uSoftness: THREE.IUniform<number>;
  uNoiseScale: THREE.IUniform<number>;
  uNoiseAmount: THREE.IUniform<number>;
  uTime: THREE.IUniform<number>;
  /** 1 = soft-particle fade active, 0 = bypass it entirely. Lets the LOW quality tier switch the
   *  depth prepass off at runtime without the clouds fading against a stale depth buffer. */
  uSoftAmount: THREE.IUniform<number>;
}

const SOFT_FRAGMENT_HEAD = /* glsl */ `
  uniform sampler2D uDepth;
  uniform vec2 uResolution;
  uniform float uCameraNear;
  uniform float uCameraFar;
  uniform float uSoftness;
  uniform float uNoiseScale;
  uniform float uNoiseAmount;
  uniform float uTime;
  uniform float uSoftAmount;
  // Per-puff noise phase. Instanced, so it arrives as a VARYING from the vertex stage rather
  // than a per-material uniform (which is what forced 900 separate materials before).
  varying float vPhase;
  varying float vViewZ;

  // Own copy rather than three's packing chunk — the sprite shader doesn't pull packing in,
  // and including it here would risk duplicate definitions if three ever adds it. Returns a
  // NEGATIVE view-space z (three's convention).
  float cloudPerspectiveDepthToViewZ(float invClipZ, float near, float far) {
    return (near * far) / ((far - near) * invClipZ - far);
  }

  float cloudHash(vec2 p) {
    p = fract(p * vec2(123.34, 345.45));
    p += dot(p, p + 34.345);
    return fract(p.x * p.y);
  }
  float cloudNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = cloudHash(i);
    float b = cloudHash(i + vec2(1.0, 0.0));
    float c = cloudHash(i + vec2(0.0, 1.0));
    float d = cloudHash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  float cloudFbm(vec2 p) {
    float v = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 3; i++) {
      v += amp * cloudNoise(p);
      p = p * 2.07 + 5.3;
      amp *= 0.5;
    }
    return v;
  }
`;

const SOFT_FRAGMENT_BODY = /* glsl */ `
  {
    // Distance from the eye to the SOLID scene behind this pixel, and to the puff itself.
    vec2 softUV = gl_FragCoord.xy / uResolution;
    float sceneZ = -cloudPerspectiveDepthToViewZ(texture2D(uDepth, softUV).x, uCameraNear, uCameraFar);
    float delta = sceneZ - vViewZ; // > 0 : puff is in front of the geometry

    // 0 right at the surface -> 1 once the puff is uSoftness world units clear of it.
    float soft = clamp(delta / max(uSoftness, 0.0001), 0.0, 1.0);

    // Fray the contact zone. The (1.0 - soft) weight limits erosion to where the puff
    // actually meets geometry, so the body of the cloud stays intact.
    //
    // vMapUv is declared by this material's own vertex shader (see VERTEX_SHADER). The name is
    // kept from the previous stock-sprite injection, where it was three's own emitted varying.
    float n = cloudFbm(vMapUv * uNoiseScale + vPhase + uTime);
    float eroded = soft - (1.0 - soft) * n * uNoiseAmount;

    // uSoftAmount 0 bypasses the fade entirely (depth prepass switched off on the low tier), so
    // the puff never fades against a stale depth buffer.
    float softAlpha = smoothstep(0.0, 1.0, clamp(eroded, 0.0, 1.0));
    diffuseColor.a *= mix(1.0, softAlpha, uSoftAmount);
  }
`;

/**
 * BILLBOARD VERTEX SHADER for the instanced puff field.
 *
 * Reproduces exactly what `THREE.Sprite` did for free, but for all puffs in ONE draw call:
 * the instance origin is transformed to view space, then the quad corner is offset in that
 * space (which is what makes it face the camera and still scale with perspective, matching
 * Sprite's default `sizeAttenuation`), with the per-puff spin applied to the corner.
 *
 * The distance haze + edge fades are computed HERE rather than on the CPU. They are pure
 * functions of the puff's XZ distance from the field centre, so moving them onto the GPU
 * removes two per-frame attribute uploads and all the per-puff colour maths, leaving only the
 * drift/wrap (which genuinely needs CPU state) to upload.
 */
const VERTEX_SHADER = /* glsl */ `
  attribute vec3 aOffset;
  attribute vec2 aScale;
  attribute float aRotation;
  attribute float aPhase;
  attribute vec3 aBaseColor;
  attribute float aBaseOpacity;

  uniform float uOpacity;
  uniform vec3 uHazeColor;
  uniform float uHazeStart;
  uniform float uHazeEnd;
  uniform float uFadeStart;
  uniform float uWrapAt;
  uniform float uInnerStart;
  uniform float uInnerEnd;

  varying vec2 vMapUv;
  varying vec3 vColor;
  varying float vOpacity;
  varying float vPhase;
  varying float vViewZ;

  void main() {
    vMapUv = uv;
    vPhase = aPhase;

    float distXZ = length(aOffset.xz);
    // Atmospheric haze — distant puffs recede toward the sky's horizon colour.
    vColor = mix(aBaseColor, uHazeColor, smoothstep(uHazeStart, uHazeEnd, distXZ));
    // Fade in from the clear zone near the camera, and out again at the field edge (so the
    // wrap in update() happens at ~0 opacity and is never visible).
    // NOTE: never write backticks in these GLSL comments — they close the template literal and
    // silently truncate the shader (TypeScript still compiles; the shader just loses its tail).
    float innerFade = smoothstep(uInnerStart, uInnerEnd, distXZ);
    vOpacity = aBaseOpacity * uOpacity * innerFade * (1.0 - smoothstep(uFadeStart, uWrapAt, distXZ));

    // Billboard: offset the corner in VIEW space so the quad always faces the eye.
    vec4 mv = modelViewMatrix * vec4(aOffset, 1.0);
    float c = cos(aRotation);
    float s = sin(aRotation);
    vec2 p = position.xy * aScale; // aScale.x may be NEGATIVE — that is the mirror coin-flip
    mv.xy += vec2(p.x * c - p.y * s, p.x * s + p.y * c);

    vViewZ = -mv.z;
    gl_Position = projectionMatrix * mv;
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  #include <common>
  // NOTE: do NOT include <tonemapping_pars_fragment> or <colorspace_pars_fragment> here.
  // three's ShaderMaterial fragment prefix already injects both, so including them again
  // redefines every tone-mapping/transfer function ("function already has a body") and the
  // material silently fails to compile — which is exactly why the clouds vanished (2026-07-19).
  // The APPLY chunks at the end of main() are not auto-injected, so those stay.

  uniform sampler2D map;
  // CLOUD V2 (sunlit look) — pure uniform toggle, see settings.cloudSea.v2. uV2SunView is the
  // sun direction already rotated into VIEW space on the CPU (the quads are view-facing
  // billboards, so their fake normals live in view space too — lighting must match).
  uniform float uV2;
  uniform vec3 uV2SunView;
  uniform vec3 uV2SunColor;
  uniform vec3 uV2Shade;
  uniform float uV2Power;
  // STORY MODE dark grade on the cloud TEXELS themselves (2026-09-01, user: "on shader mode
  // make the cloud colors darker"). 0 = authored, 1 = fully dark.
  uniform float uStoryDark;
  uniform vec3 uStoryCloudColor;
  varying vec2 vMapUv;
  varying vec3 vColor;
  varying float vOpacity;

  ${SOFT_FRAGMENT_HEAD}

  void main() {
    vec4 texel = texture2D(map, vMapUv);
    vec4 diffuseColor = vec4(vColor, vOpacity) * texel;
    // Cheap early-out for fully transparent texels — the puff texture is mostly empty, so this
    // saves the soft-particle depth fetch + fBM on the majority of the quad's fragments.
    if (diffuseColor.a < 0.003) discard;

    // V2: light each puff as a soft sphere. The quad's UV gives a fake ball normal (z from the
    // circle equation, biased so edges never go fully sideways), wrap-lit by the sun — warm on
    // the lit side, cool blue-grey in the "crevices" — which turns the flat photo sprites into
    // the reference's dense, sunlight-absorbing cumulus. Texture detail still multiplies through.
    if (uV2 > 0.5) {
      vec2 cuv = vMapUv - 0.5;
      float r2 = dot(cuv, cuv);
      vec3 ball = normalize(vec3(cuv * 1.6, sqrt(max(0.05, 0.25 - r2))));
      float ndl = clamp(dot(ball, uV2SunView) * 0.5 + 0.5, 0.0, 1.0); // wrap lighting
      ndl = pow(ndl, uV2Power);
      diffuseColor.rgb *= mix(uV2Shade, uV2SunColor, ndl);
    }

    ${SOFT_FRAGMENT_BODY}

    // STORY DARKENING - a grade on the cloud texel itself, not just the distant haze
    // (2026-09-01, user: "use postprocessing on the cloud texture, make it dark on shader
    // mode"). Luminance times the story charcoal keeps every wisp of internal structure while
    // crushing the glow, so the sea reads as dark smoke banks instead of white clouds floating
    // over a black scene.
    if (uStoryDark > 0.001) {
      float cloudLuma = dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
      diffuseColor.rgb = mix(diffuseColor.rgb, uStoryCloudColor * cloudLuma, uStoryDark);
    }

    gl_FragColor = diffuseColor;
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

/** Resolve how many puffs to actually spawn. The whole field is ONE draw call now, so
 *  `settings.cloudSea.maxPuffs` bounds instance count (vertex + fill work) rather than draw calls.
 *
 *  GOTCHA (2026-07-17): this used to be a hard-coded `MAX_PUFFS = 300` while `count` was 420 —
 *  so the config asked for 420 and silently got 300, and the GUI's 300-500 range did nothing.
 *  The cap is now a real setting, and the GUI clamps `count`'s slider to it. */
const resolvePuffCount = (): number =>
  Math.min(settings.cloudSea.count, settings.cloudSea.maxPuffs);

/**
 * "Endless" cloud sea — camera-facing billboard sprite puffs (the mrdoob
 * https://mrdoob.com/lab/javascript/webgl/clouds/ technique: plane geometry + a soft
 * cloud alpha texture, NOT a volumetric raymarch). The whole field is ONE instanced
 * draw call: a unit quad billboarded in the vertex shader (see {@link VERTEX_SHADER}),
 * which reproduces what `THREE.Sprite` used to do per-puff — always facing the camera,
 * still scaling with perspective — without a Sprite (and a material) per puff.
 *
 * The puff texture is the user-supplied `public/textures/Cloud_Texture.png` (a real
 * soft cloud photo/render with alpha) rather than a procedural stand-in — each instance
 * gets a random rotation + a coin-flip horizontal mirror so hundreds of puffs sampling
 * the same image don't read as obviously repeated.
 *
 * Colour is NOT an independent cloud palette: `lightColor`/`darkTint` are tuned to
 * sit naturally against `settings.sky`'s own gradient ("use my sky as reference"),
 * and a HAZE blend — puffs fade toward the sky's horizon colour as they recede from
 * the camera (on top of the existing opacity fade) — gives real atmospheric depth
 * cueing, the "camera LOD" that sells an endless horizon instead of a bounded disc.
 *
 * "Endless": the whole field lives under `group`, which recentres onto the camera's
 * XZ position every frame (Y fixed — the sea stays at its authored world height) —
 * so the viewer can never reach an edge. Individual puffs also drift along a wind
 * direction and wrap back in from the far side once they exceed the field radius,
 * already faded to ~0 by then so the wrap is never visible.
 */
/** Scratch colour for {@link CloudSea.setStoryDark} - avoids allocating per frame. */
const CLOUD_SCRATCH = new THREE.Color();

export class CloudSea {
  readonly group = new THREE.Group();

  private readonly puffs: Puff[] = [];
  private readonly texture: THREE.Texture;
  private readonly hazeColor = new THREE.Color();

  /** ONE mesh for the entire field (2026-07-19). Every puff used to be a `THREE.Sprite` with its
   *  OWN `SpriteMaterial` — because `rotation` and `opacity` are material-level on a sprite — so
   *  900 puffs meant 900 draw calls and 900 material binds every frame, and the cloud sea is
   *  global (never gated by the per-scene visibility system), so it cost that on every frame of
   *  every chapter. Those per-puff values are per-instance attributes here instead. */
  private mesh: THREE.Mesh | null = null;
  private geometry: THREE.InstancedBufferGeometry | null = null;
  private readonly material: THREE.ShaderMaterial;
  /** Per-frame-updated instance attribute (drift + wrap). The rest are written once at spawn. */
  private offsets: THREE.InstancedBufferAttribute | null = null;
  private baseColors: THREE.InstancedBufferAttribute | null = null;

  /** One set of objects shared by every puff material — see the {@link SoftUniforms} note. */
  private readonly soft: SoftUniforms = {
    uDepth: { value: null },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uCameraNear: { value: 0.1 },
    uCameraFar: { value: 1000 },
    uSoftness: { value: settings.cloudSea.soft.softness },
    uNoiseScale: { value: settings.cloudSea.soft.noiseScale },
    uNoiseAmount: { value: settings.cloudSea.soft.noiseAmount },
    uTime: { value: 0 },
    uSoftAmount: { value: 1 },
  };

  constructor(
    private readonly camera: Camera,
    cloudTexture: THREE.Texture,
  ) {
    this.group.name = 'cloud-sea';
    this.group.frustumCulled = false;

    this.texture = cloudTexture;
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.needsUpdate = true;

    const cfg = settings.cloudSea;
    this.material = new THREE.ShaderMaterial({
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      fog: false,
      // Sprites are tone-mapped by default, so the clouds must stay in the tone-mapping curve
      // or their colour relationship to the sky would shift. (The baked atlases opt OUT; the
      // clouds never did.) The fragment shader includes three's own chunks to match exactly.
      toneMapped: true,
      side: THREE.DoubleSide,
      uniforms: {
        map: { value: this.texture },
        uOpacity: { value: cfg.opacity },
        uStoryDark: { value: 0 },
        uStoryCloudColor: { value: new THREE.Color(settings.storyMode.grade.cloudTint) },
        uHazeColor: { value: this.hazeColor },
        uHazeStart: { value: 0 },
        uHazeEnd: { value: 0 },
        uFadeStart: { value: 0 },
        uWrapAt: { value: 0 },
        uInnerStart: { value: 0 },
        uInnerEnd: { value: 0 },
        uV2: { value: 0 },
        uV2SunView: { value: new THREE.Vector3(0, 1, 0) },
        uV2SunColor: { value: new THREE.Color() },
        uV2Shade: { value: new THREE.Color() },
        uV2Power: { value: 1.7 },
        ...this.soft,
      },
    });

    this.build();
  }

  /** Build the instanced field from the current puff config. */
  private build(): void {
    const count = resolvePuffCount();
    this.puffs.length = 0;
    for (let i = 0; i < count; i++) this.puffs.push(this.spawnPuff());

    const offsets = new Float32Array(count * 3);
    const scales = new Float32Array(count * 2);
    const rotations = new Float32Array(count);
    const phases = new Float32Array(count);
    const colors = new Float32Array(count * 3);
    const opacities = new Float32Array(count);

    const cfg = settings.cloudSea;
    for (let i = 0; i < count; i++) {
      const puff = this.puffs[i]!;
      offsets[i * 3] = puff.pos.x;
      offsets[i * 3 + 1] = puff.pos.y;
      offsets[i * 3 + 2] = puff.pos.z;

      const scale = THREE.MathUtils.lerp(cfg.minScale, cfg.maxScale, Math.random());
      // Mirror ~half the puffs and jitter the aspect a little so the same source image
      // doesn't read as obviously stamped-out across hundreds of instances.
      scales[i * 2] = scale * (Math.random() < 0.5 ? -1 : 1);
      scales[i * 2 + 1] = scale * THREE.MathUtils.lerp(0.85, 1.15, Math.random());

      rotations[i] = Math.random() * Math.PI * 2; // a real photo puff — full spin reads fine
      phases[i] = Math.random() * 100; // so their noise fields don't march in lockstep
      opacities[i] = puff.baseOpacity;
    }

    const plane = new THREE.PlaneGeometry(1, 1);
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.index = plane.index;
    geometry.setAttribute('position', plane.attributes.position!);
    geometry.setAttribute('uv', plane.attributes.uv!);
    geometry.instanceCount = count;

    this.offsets = new THREE.InstancedBufferAttribute(offsets, 3);
    this.offsets.setUsage(THREE.DynamicDrawUsage); // rewritten every frame by `update`
    this.baseColors = new THREE.InstancedBufferAttribute(colors, 3);
    geometry.setAttribute('aOffset', this.offsets);
    geometry.setAttribute('aScale', new THREE.InstancedBufferAttribute(scales, 2));
    geometry.setAttribute('aRotation', new THREE.InstancedBufferAttribute(rotations, 1));
    geometry.setAttribute('aPhase', new THREE.InstancedBufferAttribute(phases, 1));
    geometry.setAttribute('aBaseColor', this.baseColors);
    geometry.setAttribute('aBaseOpacity', new THREE.InstancedBufferAttribute(opacities, 1));

    const mesh = new THREE.Mesh(geometry, this.material);
    mesh.name = 'cloud-puffs';
    // The field is recentred on the camera every frame and is deliberately huge; a bounding
    // sphere would be meaningless and could cull the whole thing at once.
    mesh.frustumCulled = false;
    mesh.renderOrder = -1; // backdrop: behind water/architecture

    this.geometry = geometry;
    this.mesh = mesh;
    this.group.add(mesh);
    this.retint();
  }

  private spawnPuff(): Puff {
    const cfg = settings.cloudSea;
    const angle = Math.random() * Math.PI * 2;
    // Uniform-area sample over an ANNULUS (innerRadius..radius), not the full disc —
    // keeps a clear zone directly under/around the platform so it floats visibly
    // above the sea instead of getting buried by a puff spawning right beneath it.
    const inner2 = cfg.innerRadius * cfg.innerRadius;
    const dist = Math.sqrt(inner2 + Math.random() * (cfg.radius * cfg.radius - inner2));
    const y = cfg.bandBottom + Math.random() * (cfg.bandTop - cfg.bandBottom);

    const litFactor = THREE.MathUtils.clamp((y - cfg.bandBottom) / (cfg.bandTop - cfg.bandBottom), 0, 1);
    return {
      pos: new THREE.Vector3(Math.cos(angle) * dist, y, Math.sin(angle) * dist),
      baseColor: new THREE.Color(cfg.darkTint).lerp(new THREE.Color(cfg.lightColor), litFactor),
      baseOpacity: THREE.MathUtils.lerp(cfg.minOpacity, cfg.maxOpacity, Math.random()),
    };
  }

  /** Hand the clouds the frame's captured scene depth (see {@link SceneDepth}). */
  setDepthTexture(texture: THREE.Texture | null): void {
    this.soft.uDepth.value = texture;
  }

  /**
   * Bypass the soft-particle fade for the duration of a NESTED render pass (the water's planar
   * reflection — wired in `World.setupWater`). The fade samples the MAIN camera's screen-space
   * depth via gl_FragCoord/uResolution; inside a reflection render both the viewpoint and the
   * target resolution are different, so those lookups hit arbitrary texels and visibly mangled
   * the clouds' reflection (2026-07-20, user: the mirror must "reflect all objects, all mesh,
   * all sky, everything"). Writes the shared uniform DIRECTLY so the flip takes effect for the
   * nested draw happening this same frame, between this mesh's own draws.
   */
  setSoftBypass(on: boolean): void {
    this.softBypass = on;
    if (on) {
      this.soft.uSoftAmount.value = 0;
    } else {
      const cfg = settings.cloudSea;
      this.soft.uSoftAmount.value = cfg.soft.enabled && this.soft.uDepth.value ? 1 : 0;
    }
  }
  private softBypass = false;

  /** Keep the depth lookup aligned with the canvas — screen UV is derived from
   *  `gl_FragCoord / uResolution`, so this must be the DRAWING BUFFER size (canvas px ×
   *  pixel ratio), not the CSS size, or the clouds sample the wrong texels. */
  setResolution(width: number, height: number): void {
    this.soft.uResolution.value.set(Math.max(1, width), Math.max(1, height));
  }

  /** Per-chapter cloud/fog layer height (user control, 2026-07-23): the sea eases toward
   *  this Y offset (added on top of `settings.cloudSea.baseY`) whenever a chapter begins. */
  setSectionY(y: number): void {
    this.sectionYTarget = y;
  }
  private sectionY = 0;
  private sectionYTarget = 0;

  update(tick: TickState): void {
    const cfg = settings.cloudSea;
    const cam = this.camera.instance;
    // Per-chapter Y drifts smoothly toward its target (user control, 2026-07-23) — a slow
    // exponential ease so a chapter change reads as the sea settling, never a jump.
    this.sectionY += (this.sectionYTarget - this.sectionY) * (1 - Math.exp(-tick.delta * 0.9));
    // X/Z track the camera so the field is endless; Y = the global base lift + the current
    // chapter's own offset (the old scroll-rise was removed 2026-07-23 at the user's request).
    this.group.position.set(cam.position.x, cfg.baseY + this.sectionY, cam.position.z);

    // Keep the soft-particle uniforms live (camera planes can change; the GUI can retune).
    const perspective = cam as THREE.PerspectiveCamera;
    this.soft.uCameraNear.value = perspective.near;
    this.soft.uCameraFar.value = perspective.far;
    this.soft.uSoftness.value = cfg.soft.softness;
    this.soft.uNoiseScale.value = cfg.soft.noiseScale;
    this.soft.uNoiseAmount.value = cfg.soft.noiseAmount;
    // Live quality-tier toggle. Also requires a real depth texture: the soft code is always
    // compiled into this shader now (it used to be conditionally injected at spawn), so if the
    // prepass was never created — `soft.enabled` false at boot — sampling uDepth would read an
    // unbound texture. Gating on both keeps the toggle live-safe in either direction.
    // (`softBypass` is only ever true INSIDE a nested reflection render — see setSoftBypass.)
    this.soft.uSoftAmount.value = !this.softBypass && cfg.soft.enabled && this.soft.uDepth.value ? 1 : 0;
    this.soft.uTime.value += tick.delta * cfg.soft.noiseSpeed;

    const dx = cfg.windDir[0] * cfg.speed * tick.delta;
    const dz = cfg.windDir[2] * cfg.speed * tick.delta;
    const wrapAt = cfg.radius;
    const fadeStart = wrapAt * 0.75;
    // Atmospheric haze: starts blending toward the sky's horizon colour well before
    // the opacity fade kicks in, so distant puffs visibly recede into the sky instead
    // of just shrinking — the "camera LOD" depth cue that sells an endless horizon.
    const hazeStart = wrapAt * 0.35;
    const hazeEnd = wrapAt * 0.95;
    // Puffs only SPAWN outside `innerRadius`, but drift can carry one back through
    // that zone later — fade it out over `innerFade` world units as it approaches
    // the camera instead of a hard pop, so the "clear near the camera" ask holds up
    // over time, not just at spawn.
    const innerFadeStart = cfg.innerRadius;
    const innerFadeEnd = cfg.innerRadius + cfg.innerFade;

    // The haze/fade curves are evaluated per-vertex on the GPU now — push their bounds as
    // uniforms instead of recomputing a colour and an opacity per puff on the CPU.
    const u = this.material.uniforms;
    u.uOpacity!.value = cfg.opacity;
    u.uHazeStart!.value = hazeStart;
    u.uHazeEnd!.value = hazeEnd;
    u.uFadeStart!.value = fadeStart;
    u.uWrapAt!.value = wrapAt;
    u.uInnerStart!.value = innerFadeStart;
    u.uInnerEnd!.value = innerFadeEnd;

    // V2 sunlit look — all live-tunable, synced every frame like the rest. The sun direction is
    // rotated into VIEW space here (transformDirection = rotation only + renormalize) because
    // the billboards' fake normals are view-space by construction.
    const v2 = cfg.v2;
    u.uV2!.value = v2.enabled ? 1 : 0;
    if (v2.enabled) {
      (u.uV2SunView!.value as THREE.Vector3)
        .set(v2.sunDir[0], v2.sunDir[1], v2.sunDir[2])
        .normalize()
        .transformDirection(cam.matrixWorldInverse);
      (u.uV2SunColor!.value as THREE.Color).set(v2.sunColor);
      (u.uV2Shade!.value as THREE.Color).set(v2.shadeColor);
      u.uV2Power!.value = v2.power;
    }

    // Drift + wrap genuinely needs CPU state, so this is the one attribute uploaded per frame.
    const offsets = this.offsets;
    if (!offsets) return;
    const array = offsets.array as Float32Array;
    for (let i = 0; i < this.puffs.length; i++) {
      const pos = this.puffs[i]!.pos;
      pos.x += dx;
      pos.z += dz;

      if (Math.hypot(pos.x, pos.z) > wrapAt) {
        // Wrap to the opposite edge of the field — it's already faded to ~0 opacity
        // by the time it crosses `wrapAt`, so the jump is invisible.
        pos.x = -pos.x * 0.96;
        pos.z = -pos.z * 0.96;
      }

      array[i * 3] = pos.x;
      array[i * 3 + 1] = pos.y;
      array[i * 3 + 2] = pos.z;
    }
    offsets.needsUpdate = true;
  }

  /** Re-tint every puff live (height-based light/dark blend + sky haze) — no rebuild. */
  /**
   * Pull the cloud sea toward the story's night look: darker haze, lower opacity so the band
   * recedes instead of glowing white over a graded scene. `k` 0 = authored, 1 = fully story.
   * Re-derived from settings each call for the same reason as {@link Sky.setStoryDark}.
   */
  setStoryDark(k: number): void {
    const g = settings.storyMode.grade;
    const u = this.material.uniforms;
    (u.uHazeColor!.value as THREE.Color).set(settings.sky.horizon).lerp(CLOUD_SCRATCH.set(g.cloudTint), k);
    u.uOpacity!.value = settings.cloudSea.opacity * (1 - k * (1 - g.cloudOpacity));
    // The texel grade (2026-09-01): haze only darkened DISTANT puffs — the near field kept its
    // bright base colours and glowed white over the black scene. This darkens every fragment.
    u.uStoryDark!.value = k;
    (u.uStoryCloudColor!.value as THREE.Color).set(g.cloudTint);
  }

  applySettings(): void {
    this.retint();
  }

  /** Respawn every puff from the current config — needed for anything that affects
   *  puff COUNT/placement/size (count, radius, band, scale/opacity ranges), which
   *  can't be tweaked on already-spawned sprites. */
  rebuild(): void {
    this.teardownMesh();
    this.build();
  }

  /** Push the height-based light/dark tint into the static `aBaseColor` attribute. The distance
   *  haze is mixed on top of this per-vertex in the shader. */
  private retint(): void {
    const cfg = settings.cloudSea;
    const light = new THREE.Color(cfg.lightColor);
    const dark = new THREE.Color(cfg.darkTint);
    this.hazeColor.set(settings.sky.horizon);

    const attr = this.baseColors;
    if (!attr) return;
    const array = attr.array as Float32Array;
    for (let i = 0; i < this.puffs.length; i++) {
      const puff = this.puffs[i]!;
      const litFactor = THREE.MathUtils.clamp(
        (puff.pos.y - cfg.bandBottom) / (cfg.bandTop - cfg.bandBottom),
        0,
        1,
      );
      puff.baseColor.copy(dark).lerp(light, litFactor);
      array[i * 3] = puff.baseColor.r;
      array[i * 3 + 1] = puff.baseColor.g;
      array[i * 3 + 2] = puff.baseColor.b;
    }
    attr.needsUpdate = true;
  }

  /** Drop the current mesh + geometry (the material is reused across rebuilds). */
  private teardownMesh(): void {
    if (this.mesh) this.group.remove(this.mesh);
    this.geometry?.dispose();
    this.mesh = null;
    this.geometry = null;
    this.offsets = null;
    this.baseColors = null;
  }

  dispose(): void {
    this.teardownMesh();
    this.material.dispose();
  }
}
